<template>
  <v-dialog v-model="localDialog" persistent scrollable max-width="920px">
    <v-card class="profile-dialog-card entity-form-dialog material-template-dialog">
      <div class="profile-dialog-header entity-form-dialog__header">
        <div class="entity-form-dialog__title">
          <v-avatar size="44" color="green lighten-5">
            <v-icon color="primary">{{ formOpen ? "mdi-file-document-edit-outline" : "mdi-file-multiple-outline" }}</v-icon>
          </v-avatar>
          <div>
            <span>Classroom materials</span>
            <h2>{{ formOpen ? (editingId ? "Edit material template" : "Create material template") : "Material templates" }}</h2>
            <p>{{ formOpen ? "Define the questions teachers complete for a term." : "Create reusable forms for tracking materials covered." }}</p>
          </div>
        </div>
        <v-btn icon :aria-label="formOpen ? 'Back to material templates' : 'Close material templates'" @click.stop="formOpen ? resetForm() : close()">
          <v-icon>{{ formOpen ? "mdi-arrow-left" : "mdi-close" }}</v-icon>
        </v-btn>
      </div>

      <v-divider />

      <v-card-text class="profile-dialog-body entity-form-dialog__body material-template-dialog__body">
        <template v-if="!formOpen">
          <div class="material-template-dialog__toolbar">
            <v-text-field
              v-model="search"
              outlined
              dense
              hide-details
              label="Search templates"
              prepend-inner-icon="mdi-magnify"
              clearable
              @input="queueSearch"
            />
            <v-btn color="primary" depressed @click="openCreate">
              <v-icon left small>mdi-plus</v-icon>
              Create template
            </v-btn>
          </div>

          <v-progress-linear v-if="loading" indeterminate color="primary" class="mt-4" />

          <div v-if="!loading && items.length" class="material-template-list">
            <article v-for="item in items" :key="item.id" class="material-template-card">
              <div class="material-template-card__icon">
                <v-icon color="primary">mdi-clipboard-text-outline</v-icon>
              </div>
              <div class="material-template-card__copy">
                <div>
                  <h3>{{ item.name }}</h3>
                  <v-chip x-small label :color="item.isActive === false ? 'grey lighten-2' : 'green lighten-5'"
                    :class="item.isActive === false ? 'grey--text text--darken-1' : 'primary--text'">
                    {{ item.isActive === false ? "Inactive" : "Active" }}
                  </v-chip>
                </div>
                <p>{{ item.description || "No description provided." }}</p>
                <span>
                  <v-icon size="15">mdi-help-circle-outline</v-icon>
                  {{ questionCount(item) }} {{ questionCount(item) === 1 ? "question" : "questions" }}
                </span>
              </div>
              <div class="material-template-card__actions">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small v-bind="attrs" v-on="on" :aria-label="`Edit ${item.name}`" @click="openEdit(item)">
                      <v-icon size="18">mdi-pencil-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Edit template</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small color="error" v-bind="attrs" v-on="on" :aria-label="`Delete ${item.name}`" @click="remove(item)">
                      <v-icon size="18">mdi-delete-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Delete template</span>
                </v-tooltip>
              </div>
            </article>
          </div>

          <div v-if="!loading && !items.length" class="material-template-empty">
            <div class="material-template-empty__icon">
              <v-icon size="32" color="primary">mdi-file-plus-outline</v-icon>
            </div>
            <h3>{{ search ? "No matching templates" : "Create your first material template" }}</h3>
            <p>{{ search ? "Try another search term." : "Templates give teachers a consistent set of questions for recording what was covered." }}</p>
            <v-btn v-if="!search" color="primary" depressed @click="openCreate">
              <v-icon left small>mdi-plus</v-icon>
              Click to create template
            </v-btn>
          </div>
        </template>

        <template v-else>
          <section class="entity-form-dialog__section">
            <div class="entity-form-dialog__section-heading">
              <span><v-icon size="19" color="primary">mdi-file-document-outline</v-icon></span>
              <div>
                <h3>Template details</h3>
                <p>Give teachers a clear name and explanation.</p>
              </div>
              <v-switch v-model="form.isActive" inset hide-details label="Active" />
            </div>

            <v-row>
              <v-col cols="12">
                <v-text-field v-model="form.name" outlined dense hide-details label="Template name"
                  placeholder="For example: Weekly Quran progress" />
              </v-col>
              <v-col cols="12">
                <v-textarea v-model="form.description" outlined dense hide-details auto-grow rows="2"
                  label="Description" placeholder="Explain when this template should be used" />
              </v-col>
            </v-row>
          </section>

          <section class="entity-form-dialog__section">
            <div class="entity-form-dialog__section-heading">
              <span><v-icon size="19" color="primary">mdi-format-list-checks</v-icon></span>
              <div>
                <h3>Questions</h3>
                <p>Teachers will answer these when recording term materials.</p>
              </div>
              <span class="material-template-question-count">{{ form.fields.length }}</span>
            </div>

            <div class="material-template-question-list">
              <div v-for="(q, idx) in form.fields" :key="q.key || idx" class="material-template-question">
                <div class="material-template-question__number">{{ idx + 1 }}</div>
                <div class="material-template-question__fields">
                  <v-text-field v-model="q.label" outlined dense hide-details label="Question"
                    placeholder="What should the teacher record?" @blur="ensureKey(q)" />
                  <v-select v-model="q.type" :items="fieldTypeOptions" item-text="text" item-value="value"
                    outlined dense hide-details label="Answer type" />
                  <v-switch v-model="q.required" inset hide-details label="Required" />
                </div>
                <v-btn icon small color="error" :disabled="form.fields.length === 1"
                  :aria-label="`Remove question ${idx + 1}`" @click="removeField(idx)">
                  <v-icon size="18">mdi-delete-outline</v-icon>
                </v-btn>
              </div>
            </div>

            <button type="button" class="material-template-add-question" @click="addField">
              <v-icon color="primary" size="19">mdi-plus-circle-outline</v-icon>
              Add another question
            </button>
          </section>
        </template>
      </v-card-text>

      <v-divider />

      <v-card-actions class="profile-dialog-actions entity-form-dialog__actions">
        <template v-if="formOpen">
          <v-btn text @click="resetForm">Cancel</v-btn>
          <v-btn color="primary" depressed :loading="saving" @click="save">
            <v-icon left small>mdi-content-save-outline</v-icon>
            {{ editingId ? "Save changes" : "Create template" }}
          </v-btn>
        </template>
        <v-btn v-else text @click="close">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import termMaterialsService from "@/modules/TermMaterials/service";

export default {
  name: "TermMaterialTemplates",
  props: {
    dialog: { default: false },
  },
  data() {
    return {
      localDialog: false,
      search: "",
      loading: false,
      saving: false,
      items: [],
      searchTimer: null,
      fieldTypeOptions: [
        { text: "Short answer", value: "text" },
        { text: "Long answer", value: "textarea" },
      ],
      formOpen: false,
      editingId: null,
      form: {
        name: "",
        description: "",
        fields: [],
        isActive: true,
      },
    };
  },
  watch: {
    dialog(val) {
      this.localDialog = val;
      if (val) this.getList();
    },
  },
  beforeDestroy() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  },
  methods: {
    close() {
      this.resetForm();
      this.$emit("close");
    },

    resetForm() {
      this.formOpen = false;
      this.editingId = null;
      this.form = { name: "", description: "", fields: [], isActive: true };
    },

    questionCount(item) {
      return item && Array.isArray(item.fields) ? item.fields.length : 0;
    },

    queueSearch() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.getList();
      }, 250);
    },

    async getList() {
      this.loading = true;
      try {
        const res = await termMaterialsService.getTemplates({
          search: this.search || undefined,
        });
        
        this.items = (res && res.data && res.data) ? res.data : [];
      } finally {
        this.loading = false;
      }
    },

    openCreate() {
      this.resetForm();
      this.formOpen = true;
      this.addField();
    },

    openEdit(item) {
      this.formOpen = true;
      this.editingId = item.id;
      this.form = {
        name: item.name || "",
        description: item.description || "",
        fields: Array.isArray(item.fields)
          ? item.fields.map(field => ({ ...field }))
          : [],
        isActive: item.isActive !== false,
      };
      if (!this.form.fields.length) this.addField();
    },

    addField() {
      this.form.fields.push({
        key: "",
        label: "",
        type: "text",
        required: true,
      });
    },

    removeField(idx) {
      this.form.fields.splice(idx, 1);
    },

    ensureKey(q) {
      if (!q) return;
      if (q.key) return;
      const label = (q.label || "").trim().toLowerCase();
      const safe = label.replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
      q.key = safe || `q_${Date.now()}`;
    },

    validate() {
      if (!this.form.name || !this.form.name.trim()) return "Template name is required.";
      if (!Array.isArray(this.form.fields) || !this.form.fields.length) return "Add at least 1 question.";
      for (let i = 0; i < this.form.fields.length; i++) {
        const q = this.form.fields[i];
        if (!q.label || !q.label.trim()) return `Question ${i + 1} is empty.`;
        this.ensureKey(q);
      }
      return null;
    },

    async save() {
      const err = this.validate();
      if (err) {
        this.$swal.mixin({ toast: true, position: "top-end", showConfirmButton: false, timer: 3000 })
          .fire({ icon: "error", title: err });
        return;
      }

      this.saving = true;
      try {
        const payload = {
          name: this.form.name,
          description: this.form.description,
          fields: this.form.fields,
          isActive: this.form.isActive,
        };

        if (this.editingId) {
          await termMaterialsService.updateTemplate(this.editingId, payload);
        } else {
          await termMaterialsService.createTemplate(payload);
        }

        this.$swal.mixin({ toast: true, position: "top-end", showConfirmButton: false, timer: 3000 })
          .fire({ icon: "success", title: "Template saved" });

        await this.getList();
        this.resetForm();
      } catch (e) {
        this.$swal.mixin({ toast: true, position: "top-end", showConfirmButton: false, timer: 3000 })
          .fire({ icon: "error", title: (e && e.data && e.data.message) ? e.data.message : "Save failed" });
      } finally {
        this.saving = false;
      }
    },

    remove(item) {
      this.$swal({
        title: "Delete template?",
        text: "This will remove the template.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Delete",
      }).then(async (r) => {
        if (!r.value) return;
        await termMaterialsService.deleteTemplate(item.id);
        this.$swal.mixin({ toast: true, position: "top-end", showConfirmButton: false, timer: 3000 })
          .fire({ icon: "success", title: "Deleted" });
        this.getList();
      });
    },
  },
};
</script>

<style scoped>
.material-template-dialog__body {
  min-height: 420px;
}

.material-template-dialog__toolbar {
  display: flex;
  gap: 12px;
  align-items: center;
}

.material-template-dialog__toolbar .v-input {
  max-width: 420px;
  flex: 1;
}

.material-template-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 18px;
}

.material-template-card {
  display: flex;
  min-width: 0;
  min-height: 118px;
  gap: 12px;
  padding: 14px;
  align-items: flex-start;
  border: 1px solid #e0e8e2;
  border-radius: 12px;
  background: #fff;
}

.material-template-card__icon {
  display: flex;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #edf7ef;
}

.material-template-card__copy {
  min-width: 0;
  flex: 1;
}

.material-template-card__copy > div {
  display: flex;
  gap: 7px;
  align-items: center;
}

.material-template-card__copy h3 {
  overflow: hidden;
  margin: 0;
  color: #2e3a32;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.material-template-card__copy p {
  overflow: hidden;
  margin: 5px 0 8px;
  color: #7c8880;
  font-size: 10px;
  line-height: 1.45;
  text-overflow: ellipsis;
}

.material-template-card__copy > span {
  display: inline-flex;
  gap: 5px;
  align-items: center;
  color: #68756d;
  font-size: 10px;
}

.material-template-card__actions {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
}

.material-template-empty {
  display: flex;
  min-height: 320px;
  flex-direction: column;
  padding: 36px 18px;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.material-template-empty__icon {
  display: flex;
  width: 62px;
  height: 62px;
  align-items: center;
  justify-content: center;
  border-radius: 17px;
  background: #edf7ef;
}

.material-template-empty h3 {
  margin: 14px 0 4px;
  color: #2d3931;
  font-size: 16px;
}

.material-template-empty p {
  max-width: 410px;
  margin: 0 0 16px;
  color: #7a867e;
  font-size: 11px;
  line-height: 1.55;
}

.material-template-question-count {
  display: inline-flex;
  min-width: 28px;
  height: 28px;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: #2f7d4a;
  background: #e7f4ea;
  font-size: 11px;
  font-weight: 800;
}

.material-template-question-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.material-template-question {
  display: flex;
  gap: 10px;
  padding: 12px;
  align-items: center;
  border: 1px solid #e4ebe5;
  border-radius: 11px;
  background: #fafcfb;
}

.material-template-question__number {
  display: flex;
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: #2f7d4a;
  background: #e7f4ea;
  font-size: 10px;
  font-weight: 800;
}

.material-template-question__fields {
  display: grid;
  min-width: 0;
  flex: 1;
  grid-template-columns: minmax(220px, 1fr) 150px 105px;
  gap: 10px;
  align-items: center;
}

.material-template-add-question {
  display: flex;
  width: 100%;
  min-height: 46px;
  gap: 7px;
  margin-top: 12px;
  align-items: center;
  justify-content: center;
  border: 1px dashed #a9cbb2;
  border-radius: 10px;
  color: #2f7d4a;
  background: #f8fcf9;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
}

.material-template-add-question:hover,
.material-template-add-question:focus-visible {
  border-color: #2f7d4a;
  outline: none;
  background: #f0f8f2;
}

@media (max-width: 699px) {
  .material-template-dialog__body {
    min-height: 360px;
  }

  .material-template-dialog__toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .material-template-dialog__toolbar .v-input {
    max-width: none;
  }

  .material-template-dialog__toolbar .v-btn {
    width: 100%;
    margin: 0;
  }

  .material-template-list {
    grid-template-columns: 1fr;
  }

  .material-template-question {
    position: relative;
    align-items: flex-start;
  }

  .material-template-question__fields {
    grid-template-columns: 1fr;
  }

  .material-template-question > .v-btn {
    position: absolute;
    top: 6px;
    right: 6px;
  }

  .material-template-question__fields ::v-deep .v-input--switch {
    margin-top: 0;
  }
}
</style>
